export interface DailyAverageInput {
  date: Date | string;
  average: number | null;
  median?: number | null;
}

export interface DailyAveragePoint {
  date: Date;
  average: number | null;
  median: number | null;
}

const dateKey = (value: Date) => `${value.getFullYear()}-${value.getMonth()}-${value.getDate()}`;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Collapse tests into one point per calendar day. Each day's average is the
 * arithmetic mean of that day's test averages (never a sum); the median is the
 * mean of that day's test medians (falling back to the test average).
 */
export function toDailyAverages(items: DailyAverageInput[]): DailyAveragePoint[] {
  const buckets = new Map<
    string,
    { date: Date; avgSum: number; avgCount: number; medSum: number; medCount: number }
  >();

  items.forEach((item) => {
    const date = new Date(item.date);
    if (Number.isNaN(date.getTime())) return;
    const key = dateKey(date);
    const bucket =
      buckets.get(key) ?? { date, avgSum: 0, avgCount: 0, medSum: 0, medCount: 0 };

    if (isFiniteNumber(item.average)) {
      bucket.avgSum += item.average;
      bucket.avgCount += 1;
    }
    const median = item.median ?? item.average;
    if (isFiniteNumber(median)) {
      bucket.medSum += median;
      bucket.medCount += 1;
    }

    buckets.set(key, bucket);
  });

  return Array.from(buckets.values())
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((bucket) => ({
      date: bucket.date,
      average: bucket.avgCount ? bucket.avgSum / bucket.avgCount : null,
      median: bucket.medCount ? bucket.medSum / bucket.medCount : null,
    }));
}
